import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import useWorkspaceCacheUpdate from "common/datahooks/useWorkspaceCacheUpdate";
import { del, HTTPError, post } from "common/helpers/HTTP";
import { useSelectedWorkspaceContext } from "common/helpers/SelectedWorkspaceContext";
import type {
  DetailedWorkspace,
  Workspace,
  WorkspaceAccount,
} from "common/types";

import getQueryKeys from "./getQueryKeys";

async function assignLicenseRequest(
  workspaceId: string,
  accountId: string,
  abortSignal?: AbortSignal,
) {
  await post(
    `workspaces/${workspaceId}/accounts/${accountId}/license`,
    undefined,
    abortSignal,
  );
}

async function assignLicensesRequest(
  workspaceId: string,
  accountIds: string[],
): Promise<{ workspace: DetailedWorkspace; accounts: WorkspaceAccount[] }> {
  const {
    workspace: { accounts, ...detailedWorkspace },
  } = await post(`workspaces/${workspaceId}/accounts/license`, {
    account_ids: accountIds,
  });

  return { workspace: detailedWorkspace, accounts };
}

async function removeLicenseRequest(workspaceId: string, accountId: string) {
  await del(`workspaces/${workspaceId}/accounts/${accountId}/license`);
}

async function removeLicenseCancellationRequest(
  workspaceId: string,
  accountId: string,
): Promise<Workspace> {
  return (
    await post(
      `workspaces/${workspaceId}/accounts/${accountId}/license/reactivate`,
    )
  ).workspace;
}

async function setLicenseCountRequest(
  workspaceId: string,
  count: number,
  planId: string,
  accountsToCancel: string[],
): Promise<{ workspace: DetailedWorkspace; accounts: WorkspaceAccount[] }> {
  const {
    workspace: { accounts, ...detailedWorkspace },
  } = await post(`workspaces/${workspaceId}/licenses`, {
    count,
    item_price_id: planId,
    account_licenses_to_cancel: accountsToCancel,
  });

  return { workspace: detailedWorkspace, accounts };
}

export default function useLicenseMutations() {
  const queryClient = useQueryClient();

  const {
    id: workspaceId,
    accounts: workspaceAccounts,
    subscription,
  } = useSelectedWorkspaceContext();

  const { updateCachedWorkspace, updateCachedWorkspaceAccounts } =
    useWorkspaceCacheUpdate();

  function invalidateRenewEstimate() {
    queryClient.invalidateQueries({
      queryKey: getQueryKeys(workspaceId).estimate.renew(subscription),
    });
  }

  const { mutateAsync: assignLicense, isPending: isAssigningLicense } =
    useMutation({
      mutationFn: ({
        accountId,
        abortSignal,
      }: {
        accountId: string;
        abortSignal?: AbortSignal;
      }) => assignLicenseRequest(workspaceId, accountId, abortSignal),
      onSuccess: (_, { accountId }) => {
        updateCachedWorkspaceAccounts(workspaceId, (draftAccounts) => {
          const targetAccount = draftAccounts.find(
            ({ id }) => id === accountId,
          );

          if (targetAccount) {
            targetAccount.license = {
              account_id: accountId,
              status: "active",
              expires_at: 0,
              source: "aimfox",
            };
          }
        });

        updateCachedWorkspace(workspaceId, (draftWorkspace) => {
          const { licenses } = draftWorkspace;

          if (licenses) {
            const availableLicense = licenses.find(
              ({ account_id }) => !account_id,
            );

            if (availableLicense) {
              availableLicense.account_id = accountId;
            }
          }
        });

        const assignedAccount = workspaceAccounts.find(
          ({ id }) => id === accountId,
        );

        if (assignedAccount) {
          toast.success(`Seat assigned to ${assignedAccount.full_name}`);
        }
      },
    });

  const { mutateAsync: removeLicense, isPending: isRemovingLicense } =
    useMutation({
      mutationFn: ({ accountId }: { accountId: string }) =>
        removeLicenseRequest(workspaceId, accountId),
      onSuccess: (_, { accountId }) => {
        updateCachedWorkspaceAccounts(workspaceId, (draftAccounts) => {
          const targetAccount = draftAccounts.find(
            ({ id }) => id === accountId,
          );

          if (targetAccount) {
            delete targetAccount.license;
          }
        });

        updateCachedWorkspace(workspaceId, (draftWorkspace) => {
          const { licenses } = draftWorkspace;

          if (licenses) {
            const targetLicense = licenses.find(
              ({ account_id }) => account_id === accountId,
            );

            if (targetLicense) {
              targetLicense.account_id = undefined;
            }
          }
        });

        const account = workspaceAccounts.find(({ id }) => id === accountId);
        toast.success(`Unassigned seat from ${account.full_name}`);
      },
      onError: () => toast.error("Failed to remove seat"),
    });

  const {
    mutateAsync: removeLicenseCancellation,
    isPending: isRemovingLicenseCancellation,
  } = useMutation({
    mutationFn: ({ accountId }: { accountId: string }) =>
      removeLicenseCancellationRequest(workspaceId, accountId),
    onSuccess: (_, { accountId }) => {
      updateCachedWorkspaceAccounts(workspaceId, (draftAccounts) => {
        const targetAccount = draftAccounts.find(({ id }) => id === accountId);

        if (targetAccount?.license) {
          targetAccount.license.expires_at = 0;
          targetAccount.license.status = "active";
        }
      });

      updateCachedWorkspace(workspaceId, (draftWorkspace) => {
        const { licenses } = draftWorkspace;

        if (licenses) {
          const targetLicense = licenses.find(
            ({ account_id }) => account_id === accountId,
          );

          if (targetLicense) {
            targetLicense.expires_at = 0;
            targetLicense.status = "active";
          }
        }
      });

      invalidateRenewEstimate();

      toast.success("Removed license cancellation");
    },
    onError: () => toast.error("Failed to remove license cancellation"),
  });

  const { mutateAsync: setLicenseCount, isPending: isSettingLicenseCount } =
    useMutation({
      mutationFn: ({
        count,
        planId,
        accountsToCancel,
      }: {
        count: number;
        planId: string;
        accountsToCancel: string[];
      }) =>
        setLicenseCountRequest(workspaceId, count, planId, accountsToCancel),
      onSuccess: ({ workspace, accounts }) => {
        updateCachedWorkspace(workspaceId, () => {
          return workspace;
        });

        updateCachedWorkspaceAccounts(workspaceId, () => {
          return accounts;
        });

        invalidateRenewEstimate();

        toast.success("Your subscription has been updated!");
      },
      onError: (error) =>
        toast.error(
          error instanceof HTTPError && error.serverMessage
            ? error.serverMessage
            : "Failed to update subscription",
        ),
    });

  const { mutateAsync: assignLicenses, isPending: isAssigningLicenses } =
    useMutation({
      mutationFn: ({ accountIds }: { accountIds: string[] }) =>
        assignLicensesRequest(workspaceId, accountIds),
      onSuccess: ({ workspace, accounts }, { accountIds }) => {
        updateCachedWorkspace(workspaceId, () => {
          return workspace;
        });

        updateCachedWorkspaceAccounts(workspaceId, () => {
          return accounts;
        });

        // Create success message
        const assignedAccounts = workspaceAccounts.filter(({ id }) =>
          accountIds.includes(id),
        );

        if (assignedAccounts.length === 1) {
          toast.success(`Seat assigned to ${assignedAccounts[0].full_name}`);
        } else {
          toast.success(`Seats assigned to ${assignedAccounts.length} users`);
        }
      },
      onError: (error) =>
        toast.error(
          error instanceof HTTPError && error.serverMessage
            ? error.serverMessage
            : "Failed to assign seats",
        ),
    });

  return {
    assignLicense,
    isAssigningLicense,
    removeLicense,
    isRemovingLicense,
    removeLicenseCancellation,
    isRemovingLicenseCancellation,
    setLicenseCount,
    isSettingLicenseCount,
    assignLicenses,
    isAssigningLicenses,
  };
}
